"use client"

import { Web3Button } from "@abraxas/ui/web3"
import { Button } from "@abraxas/ui/ui"
import { formatEther } from "viem"
import { useBuyListing } from "./hooks"
import { Listing, NATIVE_TOKEN_ADDRESS } from "./types"
import { toast } from "sonner"
import { QuantityInput } from "@/shared/components/QuantityInput"
import React, { useState } from "react"

interface BuyListingFormProps {
  listing: Listing
  onPurchaseComplete: () => void
  onCancel: () => void
}

export const BuyListingForm: React.FC<BuyListingFormProps> = ({
  listing,
  onPurchaseComplete,
  onCancel,
}) => {
  const [quantity, setQuantity] = useState(1)

  const { buyListing, isPending, canBuy, cannotBuyReason } = useBuyListing({
    listing,
  })

  const isNativeCurrency =
    listing.currency.toLowerCase() === NATIVE_TOKEN_ADDRESS.toLowerCase()

  const priceInEth = formatEther(listing.pricePerToken)
  const totalPrice = formatEther(
    BigInt(listing.pricePerToken) * BigInt(quantity),
  )

  const handleConfirmBuy = async () => {
    try {
      await buyListing(BigInt(quantity))
      toast.success("Purchase successful!")
      onPurchaseComplete()
    } catch (error) {
      console.error("Purchase failed:", error)
      toast.error("Purchase failed. Please try again.")
    }
  }

  // Disable button if user is seller, listing is expired, or transaction is pending
  const isDisabled = !canBuy || isPending

  return (
    <div className="space-y-4">
      <div className="border-t border-b border-gray-200 py-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-brand-blue-300">Price per item:</span>
          <span className="font-semibold">{priceInEth} ETH</span>
        </div>
        <div className="flex justify-between items-center mb-4">
          <span className="text-brand-blue-300">Quantity:</span>
          <QuantityInput
            value={quantity}
            onChange={setQuantity}
            max={Number(listing.availableQuantity)}
            className="justify-end"
          />
        </div>
        <div className="flex justify-between items-center text-sm text-brand-blue-400 mb-2">
          <span>Available:</span>
          <span>{listing.availableQuantity.toString()}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-brand-blue-300">Total:</span>
          <span className="text-lg font-bold text-green-600">
            {totalPrice} ETH
          </span>
        </div>
      </div>

      <div className="text-sm text-brand-blue-400">
        <div className="mb-1">
          <strong>Seller:</strong> {listing.listingCreator.slice(0, 6)}...
          {listing.listingCreator.slice(-4)}
        </div>
        <div className="mb-1">
          <strong>Payment:</strong>{" "}
          {isNativeCurrency ? "Native ETH" : "ERC20 Token"}
        </div>
        <div>
          <strong>Expires:</strong>{" "}
          {new Date(Number(listing.endTimestamp) * 1000).toLocaleString()}
        </div>
      </div>

      {isNativeCurrency && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
          <div className="text-sm text-yellow-800">
            <strong>Gas Notice:</strong> You will need to pay for both the NFT
            and gas fees.
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Web3Button
          color="brand"
          loading={isPending}
          onClick={handleConfirmBuy}
          disabled={isDisabled}
        >
          {isPending ? "Buying..." : cannotBuyReason || "Confirm Purchase"}
        </Web3Button>
      </div>
    </div>
  )
}
